import React, { useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonLoading, FormCheck, FormInput } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { LoadingContainer } from "go-core/components/Loading";
import { FormDatePicker } from "go-form/components/FormDatePicker";
import { FormDirty } from "go-form/components/FormDirty";
import { adminApi } from "../../../../../../../../services/Api/Admin/adminApi";
import { SyncOrganizationFeatureReportApi } from "../../../../../../../../services/Api/Admin/adminTypes";

interface Props {
	isShown: boolean;
	onHide: () => void;
	onSuccessHandle: () => void;
}

const SynchronizeOrganizationFeatureUsageReportModal = ({ isShown, onHide, onSuccessHandle }: Props) => {
	const { t } = useTranslation("admin");
	const form = useForm<SyncOrganizationFeatureReportApi>({
		criteriaMode: "all",
		defaultValues: {
			type_feature: true,
			type_product: true,
		},
	});
	const { addFlash, addSuccessFlash } = useFlash();
	const {
		control,
		handleSubmit,
		setError,
		register,
		formState,
		watch,
		formState: { errors },
	} = form;
	const [loading, setLoading] = useState<boolean>(false);
	const [submitLoading, setSubmitLoading] = useState<boolean>(false);

	const getParsedFormData = (data: SyncOrganizationFeatureReportApi) => {
		const companyIds =
			data.company_ids && typeof data.company_ids === "string" ? data.company_ids.split(",") : undefined;
		data.sync_types = [];
		if (data.type_feature) data.sync_types.push("FEATURE");
		if (data.type_feature_fix) data.sync_types.push("FEATURE_FIX");
		if (data.type_product) data.sync_types.push("PRODUCT");

		return {
			...data,
			company_ids: companyIds,
		};
	};

	const onSubmit = handleSubmit(async (data) => {
		setSubmitLoading(true);
		try {
			const newData = getParsedFormData(data);
			await adminApi.syncOrganizationFeatures(newData);
			addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
			onHide();
			onSuccessHandle();
		} catch (err) {
			handleError.form(err, setError, addFlash);
		}
		setSubmitLoading(false);
	});

	if (loading) return <LoadingContainer />;

	return (
		<Modal onHide={onHide} show={isShown}>
			<Modal.Header closeButton>
				<Modal.Title>{t("modules.transaction_report.field.synchronize.title")}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<FormDirty formState={formState} onSubmit={onSubmit}>
					<FormCheck
						name={"type_product"}
						errors={errors}
						register={register}
						label={"Synchronizacja produktów"}
					/>
					<FormCheck
						name={"type_feature"}
						errors={errors}
						register={register}
						label={"Synchronizacja funkcji"}
					/>
					<FormCheck
						name={"type_feature_fix"}
						errors={errors}
						register={register}
						label={"Synchronizacja funkcji (donwgrade,upgrade)"}
					/>
					<FormDatePicker
						errors={errors}
						name={`date_from`}
						control={control}
						label={"Od daty"}
						help={"Pobierze dane zużycia od tej daty"}
					/>
					<FormDatePicker
						errors={errors}
						name={`sales_date_from`}
						control={control}
						label={"Od daty sprzedaży"}
						help={"Będzie synchronizował lokalizacje które miały sprzedaż od wybraje daty"}
					/>
					<FormInput
						label={"Company ID"}
						name={"company_ids"}
						errors={errors}
						register={register}
						help={"Ograniczy synchronizacje do wybranej lokalizacji"}
					/>
				</FormDirty>
			</Modal.Body>
			<Modal.Footer>
				<ButtonLoading loading={submitLoading} type="submit" onClick={onSubmit}>
					{t("modules.transaction_report.action.synchronize.title")}
				</ButtonLoading>
				<Button variant="light" onClick={onHide}>
					{t("common.action.cancel", { ns: "lib" })}
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export default SynchronizeOrganizationFeatureUsageReportModal;
